import { useNavigate } from 'react-router-dom';
import FileSaver from 'file-saver';
import {
    Box,
    Flex,
    Heading,
    Text,
    Button,
    Icon,
    useToast,
    useDisclosure,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Select,
} from '@chakra-ui/react';
import PageHeader from 'layout/pageHeader';
import MDropZone from 'components/molecules/MDropZone';
import { useState } from 'react';
import { EXCEL_FILE_TYPE } from 'shared/const';
import icons from 'shared/icons';
import {
    useLazyExportUsersQuery,
    useUploadUserExcelMutation,
    useExportUserUploadErrorExcelMutation,
} from 'core/API/usersApi';
import { intentionalyFloatingPromiseReturn } from 'utils/wrappers';
import { isFetchBaseQueryError } from 'utils/erros';
import { IExcelUser } from 'pages/UserManagement/types';
import { useTranslation } from 'helpers/languages/translations';

const UsersUpload = () => {
    const translate = useTranslation();
    const [usersErrors, setUsersErrors] = useState<IExcelUser[]>([]);
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [exportUserUploadErrors, { isLoading: isExportErrorsLoading }] = useExportUserUploadErrorExcelMutation();
    const [uploadUser, { isLoading: isUploadLoading }] = useUploadUserExcelMutation();
    const [exportTrigger, { isLoading: isExportLoading }] = useLazyExportUsersQuery();
    const { isOpen: isModalErrorOpen, onOpen: onOpenModalErrorUpload, onClose: onModalErrorClose } = useDisclosure();
    const toast = useToast();
    const navigate = useNavigate();
    const goBack = () => navigate(-1);
    const [isUserInternational, setIsUserInternational] = useState(false);

    const handleChangeFile = (file: File | null) => {
        if (file?.type === EXCEL_FILE_TYPE) {
            setUploadedFile(file);
        } else if (file === null) {
            setUploadedFile(null);
        }
    };

    const handleDownloadError = async () => {
        try {
            const users = await exportUserUploadErrors(usersErrors);
            if ('error' in users) {
                const errorObj = users.error;
                if (isFetchBaseQueryError(errorObj)) {
                    const errorMsg: string =
                        'message' in (errorObj.data as any)
                            ? (errorObj.data as { message: string }).message
                            : JSON.stringify(errorObj.data);
                    throw new Error(errorMsg);
                }
                throw new Error('Error while download the errors');
            }

            const blob = new Blob([users.data], {
                type: EXCEL_FILE_TYPE,
            });

            // todo: get the name of the user here or company info
            FileSaver.saveAs(blob, 'error_file.xlsx');
            onModalErrorClose();
        } catch {
            toast({
                title: "Can't download error file",
                status: 'error',
                isClosable: true,
            });
        }
    };

    const handleUpload = async () => {
        try {
            const bodyFormData = new FormData();
            bodyFormData.append('excelFile', uploadedFile as File);
            bodyFormData.append('isInternational', isUserInternational ? 'true' : 'false');

            const users = await uploadUser(bodyFormData);

            if ('error' in users) {
                const errorObj = users.error;
                if (isFetchBaseQueryError(errorObj)) {
                    const errorMsg: string =
                        'message' in (errorObj.data as any)
                            ? (errorObj.data as { message: string }).message
                            : JSON.stringify(errorObj.data);

                    if (((errorObj.data as any)?.arrayErrors as any).length) {
                        setUsersErrors((errorObj.data as any).arrayErrors as IExcelUser[]);
                        onOpenModalErrorUpload();
                    }

                    throw new Error(errorMsg);
                }
            } else {
                goBack();

                toast({
                    title: `succesfully uploaded users`,
                    status: 'success',
                    isClosable: true,
                });
            }
        } catch {
            toast({
                title: 'error uploading file',
                status: 'error',
                isClosable: true,
            });
        }
    };

    const renderModal = () => {
        return (
            <Modal isOpen={isModalErrorOpen} onClose={onModalErrorClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{translate('users.upload.modal.title')}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {translate('users.upload.modal.body_1')} {usersErrors.length}{' '}
                        {translate('users.upload.modal.body_2')}
                    </ModalBody>

                    <ModalFooter>
                        <Button mr={3} variant="ghost" onClick={onModalErrorClose}>
                            {translate('users.upload.modal.close')}
                        </Button>
                        <Button colorScheme="blue" onClick={intentionalyFloatingPromiseReturn(handleDownloadError)}>
                            {translate('users.upload.modal.validate.error')}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        );
    };

    const renderDescriptionSection = () => {
        const DownloadIcon = icons.download;
        const UploadIcon = icons.upload;

        const handleExportTemplate = async () => {
            try {
                const { data: res, isError } = await exportTrigger({ isTemplate: true, isInternational: isUserInternational });
                if (isError || !res) throw new Error('an Error occured when downloading file');

                const blob = new Blob([res], {
                    type: EXCEL_FILE_TYPE,
                });

                FileSaver.saveAs(blob, isUserInternational ? 'international_user_template.xlsx' : 'user_template.xlsx');
            } catch (error) {
                toast({
                    title: (error as Error).message,
                    status: 'error',
                    isClosable: true,
                });
            }
        };

        return (
            <Flex w="45%" justifyContent="flex-start" direction="column" textAlign="start">
                <Text>{translate('users.upload.title')}</Text>
                <Text>{translate('users.upload.body')}</Text>
                <Text fontSize="md" mt="15px">{translate('users.upload.typeSelection')}</Text>
                <Select bg="white" my={2} value={isUserInternational ? 'international' : 'local'} onChange={(e) => {
                    setIsUserInternational(e.target.value === 'international');
                }}>
                    <option value="local">{translate('users.upload.local')}</option>
                    <option value="international">{translate('users.upload.international')}</option>
                </Select>
                <Flex mt="24px" justifyContent="space-between">
                    <Button
                        isLoading={isExportLoading}
                        onClick={intentionalyFloatingPromiseReturn(handleExportTemplate)}
                        leftIcon={<Icon as={DownloadIcon} />}
                    >
                        {translate('users.upload.download.template')}
                    </Button>
                    {renderModal()}
                    <Button
                        onClick={intentionalyFloatingPromiseReturn(handleUpload)}
                        isLoading={isUploadLoading}
                        isDisabled={!uploadedFile}
                        rightIcon={<Icon as={UploadIcon} />}
                        colorScheme="blue"
                        bg="blueMain"
                    >
                        {translate('users.upload.button')}
                    </Button>
                </Flex>
            </Flex>
        );
    };

    return (
        <Box>
            <PageHeader pageTitle={`Users / Upload`} icon={{ name: 'arrowBack', props: { onClick: goBack } }} />
            <Box px="32px">
                <Flex justifyContent="start">
                    <Heading as="h2" fontWeight="semibold" fontSize="18px" mb="18px">
                        Upload File
                    </Heading>
                </Flex>
                <Flex direction="row">
                    {renderDescriptionSection()}
                    <Box mx="32px" w="55%">
                        <MDropZone chosenFile={uploadedFile} handleChangeFile={handleChangeFile} />
                    </Box>
                </Flex>
            </Box>
        </Box>
    );
};

export default UsersUpload;
